import {
  STARDOG_CONTEXT_ALL_IRI,
  STARDOG_CONTEXT_DEFAULT_IRI,
  STARDOG_CONTEXT_LOCAL_IRI,
  STARDOG_CONTEXT_NAMED_IRI,
  STARDOG_CONTEXT_VIRTUAL_IRI,
} from 'bones/constants/iris';
import { VoiceboxCopy } from 'bones/constants/voicebox/schema';
import {
  VoiceboxDatabase,
  VoiceboxNamedGraphs,
} from 'bones/constants/voicebox/settings';
import { createDivider } from 'bones/design-system/utils/select/createDivider';
import { createItem } from 'bones/design-system/utils/select/createItem';
import { SelectItem } from 'bones/design-system/utils/select/types';
import { getLocalName } from 'bones/utils/getLocalName';
import { alphaSort, alphaSorter } from 'bones/utils/sort/alphaSort';

export const SPECIAL_NAMED_GRAPH_IRIS = [
  STARDOG_CONTEXT_ALL_IRI,
  STARDOG_CONTEXT_DEFAULT_IRI,
  STARDOG_CONTEXT_LOCAL_IRI,
  STARDOG_CONTEXT_NAMED_IRI,
  STARDOG_CONTEXT_VIRTUAL_IRI,
];

export interface UseVoiceboxSettingsItemsProps {
  copy: VoiceboxCopy;
  databasesById: Record<string, VoiceboxDatabase>;
  databaseId: string;
  namedGraphsByDatabaseId: Record<string, VoiceboxNamedGraphs>;
}

export interface UseVoiceboxSettingsItemsResult {
  databaseItems: Array<SelectItem<null>>;
  namedGraphItems: Array<SelectItem<null>>;
  modelItems: Array<SelectItem<null>>;
}

export const useVoiceboxSettingsItems = ({
  copy,
  databasesById,
  databaseId,
  namedGraphsByDatabaseId,
}: UseVoiceboxSettingsItemsProps): UseVoiceboxSettingsItemsResult => {
  const databaseItems = Object.keys(databasesById)
    .sort(alphaSorter)
    .map((databaseId) => createItem(databaseId, databaseId));

  const namedGraphItems = SPECIAL_NAMED_GRAPH_IRIS.map((item) =>
    createItem(getLocalName(item), item)
  );
  const modelItems: SelectItem[] = [];

  const addIrisToNamedGraphs = (text: string, iris: string[]) => {
    namedGraphItems.push(
      createDivider(text),
      ...iris
        .map((iri) => createItem(getLocalName(iri), iri))
        .sort(alphaSort(({ text }) => text || /* istanbul ignore next */ ''))
    );
  };

  const namedGraphsData = namedGraphsByDatabaseId[databaseId];
  if (namedGraphsData) {
    const named: string[] = [];
    const aliases: string[] = [];
    const virtual: string[] = [];

    Object.values(namedGraphsData).forEach(({ iri, isAlias, isVirtual }) => {
      if (isAlias) {
        aliases.push(iri);
      } else if (isVirtual) {
        virtual.push(iri);
      } else {
        named.push(iri);
      }
    });

    if (aliases.length) {
      addIrisToNamedGraphs(
        copy.settingsAside.namedGraphs.dividers.alias,
        aliases
      );
    }
    if (named.length) {
      addIrisToNamedGraphs(
        copy.settingsAside.namedGraphs.dividers.named,
        named
      );
    }
    if (virtual.length) {
      addIrisToNamedGraphs(
        copy.settingsAside.namedGraphs.dividers.virtual,
        virtual
      );
    }
  }

  const databaseData = databasesById[databaseId];
  if (databaseData) {
    modelItems.push(
      ...[...databaseData.models]
        .sort(alphaSorter)
        .map((model) => createItem(model, model))
    );
  }

  return { databaseItems, namedGraphItems, modelItems };
};
